// src/services/mediaService.ts
import unifiedAuth from '../utils/unifiedAuth';
import type { UnifiedContentItem } from '../types/unifiedContent';
import { config } from '../config';

const API_BASE_URL = config.apiBaseUrl;

// ===== INTERFACES =====
export interface MediaFilter {
  search?: string;
  category?: string;
  tags?: string[];
  author?: string;
  status?: string;
  is_featured?: boolean;
  is_trending?: boolean;
  is_premium?: boolean;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface Author {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  name?: string; // derived field for display
}

export interface Subtitle {
  id: string;
  language: string;
  language_code: string;
  label: string;
  url: string;
  is_default?: boolean;
}

export interface MediaInteraction {
  id: string;
  interaction_type: 'like' | 'bookmark' | 'watch' | 'rate';
  rating?: number;
  watch_progress?: number;
  created_at: string;
}

// Cinema specific interfaces (extends backend Content entity)
export interface Cinema {
  id: string;
  title: string;
  slug?: string;
  description: string;
  short_description?: string;
  category: string;
  contentType: 'CINEMA';
  tags?: string[];
  thumbnailUrl: string;
  thumbnail?: string; // alias for thumbnailUrl
  mediaUrl?: string;
  previewUrl?: string;
  trailerUrl?: string;
  author: Author;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SCHEDULED';
  isFeatured: boolean;
  is_featured?: boolean; // alias for isFeatured
  isPremium: boolean;
  is_premium?: boolean; // alias for isPremium
  viewCount: number;
  view_count?: number; // alias for viewCount
  likeCount: number;
  like_count?: number; // alias for likeCount
  durationSeconds: number;
  duration?: number; // alias for durationSeconds
  duration_formatted: string;
  fileSizeMb: number;
  file_size?: number; // alias for fileSizeMb
  file_size_formatted?: string;
  subtitles?: Subtitle[];
  creatorName?: string;
  channelName?: string;
  externalId?: string;
  imdbId?: string;
  is_liked?: boolean;
  is_bookmarked?: boolean;
  user_rating?: number;
  watch_progress?: number;
  createdAt: string;
  created_at?: string; // alias for createdAt
  updatedAt: string;
  updated_at?: string; // alias for updatedAt
  publishedAt?: string;
  published_at?: string; // alias for publishedAt
  // Series-specific properties (optional - only present if content is part of a series)
  isPartOfSeries?: boolean;
  seriesId?: string;
  seriesTitle?: string;
  seasonNumber?: number;
  episodeNumber?: number;
  totalSeasons?: number;
  totalEpisodes?: number;
  seriesType?: 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES';
}

// Content specific interfaces (extends backend Content entity)
export interface Content {
  id: string;
  title: string;
  slug?: string;
  description: string;
  short_description?: string;
  category: string;
  contentType: 'CINEMA' | 'CONTENT' | 'PODCAST' | 'ANIMATION' | 'SNEAK_PEEK' | 'LIVE_VIDEO';
  content_type?: string; // alias for contentType
  tags?: string[];
  thumbnailUrl: string;
  thumbnail?: string; // alias for thumbnailUrl
  mediaUrl?: string;
  previewUrl?: string;
  trailerUrl?: string;
  contentBody?: string;
  author: Author;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SCHEDULED';
  isFeatured: boolean;
  is_featured?: boolean; // alias for isFeatured
  isPremium: boolean;
  is_premium?: boolean; // alias for isPremium
  isLive: boolean;
  is_live?: boolean; // alias for isLive
  viewCount: number;
  view_count?: number; // alias for viewCount
  likeCount: number;
  like_count?: number; // alias for likeCount
  durationSeconds: number;
  duration?: number; // alias for durationSeconds
  duration_formatted: string;
  fileSizeMb: number;
  file_size?: number; // alias for fileSizeMb
  file_size_formatted?: string;
  subtitles?: Subtitle[];
  creatorName?: string;
  creator?: string; // alias for creatorName
  channelName?: string;
  externalId?: string;
  imdbId?: string;
  liveStatus?: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  scheduledStartTime?: string;
  actualStartTime?: string;
  currentViewers?: number;
  maxViewers?: number;
  is_liked?: boolean;
  is_bookmarked?: boolean;
  user_rating?: number;
  watch_progress?: number;
  createdAt: string;
  created_at?: string; // alias for createdAt
  updatedAt: string;
  updated_at?: string; // alias for updatedAt
  publishedAt?: string;
  published_at?: string; // alias for publishedAt
  // Series-specific properties (optional - only present if content is part of a series)
  isPartOfSeries?: boolean;
  seriesId?: string;
  seriesTitle?: string;
  seasonNumber?: number;
  episodeNumber?: number;
  totalSeasons?: number;
  totalEpisodes?: number;
  seriesType?: 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES';
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface MediaStats {
  total_cinema: number;
  total_content: number;
  total_views: number;
  total_likes: number;
  trending_cinema: Cinema[];
  trending_content: Content[];
  featured_cinema: Cinema[];
  featured_content: Content[];
  recent_cinema: Cinema[];
  recent_content: Content[];
}

export interface HeroMediaResponse {
  id: string;
  title: string;
  description: string;
  short_description?: string;
  category: string;
  thumbnail: string;
  banner?: string;
  type: 'cinema' | 'content';
  duration_formatted: string;
  view_count: number;
  like_count: number;
  average_rating: number;
  is_trending: boolean;
  is_featured: boolean;
}

export interface MediaLibrary {
  watched_cinema: Cinema[];
  watched_content: Content[];
  bookmarked_cinema: Cinema[];
  bookmarked_content: Content[];
}

export interface SearchResults {
  cinema: Cinema[];
  content: Content[];
}

export interface MediaCollection {
  id: string;
  name: string;
  description: string;
  user: Author;
  cinema: Cinema[];
  content: Content[];
  is_public: boolean;
  total_items: number;
  created_at: string;
  updated_at: string;
}

export interface Playlist {
  id: string;
  name: string;
  description: string;
  user: Author;
  is_public: boolean;
  total_items: number;
  created_at: string;
  updated_at: string;
  items: PlaylistItem[];
}

export interface PlaylistItem {
  id: string;
  content_type: 'cinema' | 'content';
  object_id: string;
  order: number;
  added_at: string;
}

// Backend Content Entity interface (matches Java Content entity)
export interface BackendContent {
  id: string;
  title: string;
  slug?: string;
  description?: string;
  contentType: 'CINEMA' | 'CONTENT' | 'PODCAST' | 'ANIMATION' | 'SNEAK_PEEK' | 'LIVE_VIDEO';
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SCHEDULED';
  category?: string;
  tags?: string[];
  thumbnailUrl?: string;
  mediaUrl?: string;
  previewUrl?: string;
  trailerUrl?: string;
  contentBody?: string;
  durationSeconds?: number;
  fileSizeMb?: number;
  viewCount?: number;
  likeCount?: number;
  isFeatured?: boolean;
  isPremium?: boolean;
  isLive?: boolean;
  liveStatus?: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  scheduledStartTime?: string;
  actualStartTime?: string;
  currentViewers?: number;
  maxViewers?: number;
  author?: Author;
  creatorName?: string;
  channelName?: string;
  externalId?: string;
  imdbId?: string;
  subtitles?: Subtitle[];
  is_liked?: boolean;
  is_bookmarked?: boolean;
  user_rating?: number;
  watch_progress?: number;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  // Series-related properties
  partOfSeries?: boolean;
  seasonNumber?: number;
  episodeNumber?: number;
  episode?: {
    id: string;
    title: string;
    description: string;
    episodeNumber: number;
    season?: {
      id: string;
      title: string;
      seasonNumber: number;
    };
    series?: {
      id: string;
      title: string;
      description: string;
      seriesType: 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES';
      totalSeasons: number;
      totalEpisodes: number;
      seasons?: Array<{
        id: string;
        title: string;
        seasonNumber: number;
      }>;
    };
  };
}

// ===== HELPER FUNCTIONS =====
// Authorization is attached by unifiedAuth.fetch, which refreshes an expired token
// and retries. Attaching it here as well was redundant and made it look as though
// this file managed its own auth.
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
});

// Deliberately empty: for FormData the browser must set Content-Type itself so it
// can include the multipart boundary.
const getFormDataHeaders = (): Record<string, string> => ({});

const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const headers = options.body instanceof FormData
    ? getFormDataHeaders()
    : getAuthHeaders();

  try {
    // Token attachment + 401 refresh-retry is delegated to unifiedAuth.fetch()
    // (the single shared implementation); this function keeps its own
    // detailed, status-code-specific error message handling below.
    const response = await unifiedAuth.fetch(`${API_BASE_URL}${endpoint}`, {
      headers,
      ...options,
    });

    if (!response.ok) {
      let errorData = {};
      let errorText = '';
      
      try {
        const responseText = await response.text();
        errorText = responseText;
        
        if (responseText) {
          try {
            errorData = JSON.parse(responseText);
          } catch (_parseError) {
            // Response is not JSON, use the text as error message
            errorData = { message: responseText };
          }
        }
      } catch (_readError) {
        console.error('Failed to read error response:', _readError);
      }
      
      if (response.status === 401) {
        unifiedAuth.clearTokens();
        window.location.href = '/login';
        throw new Error('Your session has expired. Please log in again.');
      }
      
      // Improved error message construction
      let errorMessage = '';
      
      if (errorData && typeof errorData === 'object') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = errorData as any;
        
        if (data.message) {
          errorMessage = data.message;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.error) {
          errorMessage = data.error;
        }
      } else if (errorText) {
        errorMessage = errorText;
      }
      
      // Handle specific status codes with more descriptive messages
      if (response.status === 404) {
        if (endpoint.includes('/content/')) {
          const contentId = endpoint.split('/').pop();
          errorMessage = `Content not found. The requested content (ID: ${contentId}) does not exist or may have been removed.`;
        } else {
          errorMessage = errorMessage || 'The requested resource was not found.';
        }
      } else if (response.status === 403) {
        errorMessage = errorMessage || 'You do not have permission to access this content. Please log in or check your account.';
      } else if (response.status === 500) {
        errorMessage = errorMessage || 'A server error occurred while loading this content. Please try again later.';
      } else if (response.status === 408 || response.status === 504) {
        errorMessage = 'The request timed out. Please check your connection and try again.';
      } else if (response.status === 429) {
        errorMessage = 'Too many requests. Please wait a moment before trying again.';
      } else if (!errorMessage) {
        errorMessage = `Request failed (${response.status}). Please try again later.`;
      }
      
      console.error(`API Error [${response.status}] ${endpoint}:`, {
        status: response.status,
        statusText: response.statusText,
        errorData,
        errorText,
        finalMessage: errorMessage
      });
      
      throw new Error(errorMessage);
    }

    return response.json();
  } catch (networkError) {
    // Handle network errors (fetch failures)
    if (networkError instanceof Error && networkError.message === 'Failed to fetch') {
      throw new Error('Network error. Please check your internet connection and try again.');
    }
    
    // Re-throw other errors (including our custom API errors)
    throw networkError;
  }
};

// ===== MEDIA SERVICE =====
class MediaService {
  // ===== CINEMA METHODS =====
  
  // Cinema-specific methods
  async getCinema(filters: MediaFilter = {}): Promise<PaginatedResponse<Cinema>> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          // Map frontend filter keys to backend keys if needed
          let backendKey = key;
          let backendValue = value.toString();
          
          if (key === 'page_size') {
            backendKey = 'size';
          } else if (key === 'page') {
            // Convert 1-based frontend pagination to 0-based backend pagination
            backendValue = Math.max(0, parseInt(value.toString()) - 1).toString();
          } else if (key === 'status' && value === 'published') {
            backendValue = 'PUBLISHED';
          }
          
          params.append(backendKey, backendValue);
        }
      }
    });

    const queryString = params.toString();
    
    // Fetch both regular cinema content and cinema series
    const [contentResponse, seriesResponse] = await Promise.allSettled([
      apiRequest(`/content/cinema${queryString ? `?${queryString}` : ''}`),
      apiRequest(`/series/cinema${queryString ? `?${queryString}` : ''}`)
    ]);
    
    // Process regular content
    let regularContent: Cinema[] = [];
    if (contentResponse.status === 'fulfilled') {
      const pageData = contentResponse.value.data || contentResponse.value;
      regularContent = (pageData.content || pageData.results || []).map((item: BackendContent) => this.transformContentToCinema(item));
    }
    
    // Process series content and convert to Cinema items
    let seriesContent: Cinema[] = [];
    if (seriesResponse.status === 'fulfilled') {
      const seriesData = seriesResponse.value.data || seriesResponse.value;
      seriesContent = (seriesData.results || []).map((series: any) => this.transformSeriesToCinema(series)); // eslint-disable-line @typescript-eslint/no-explicit-any
    }
    
    // Combine and sort by creation date
    const allContent = [...regularContent, ...seriesContent].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    
    return {
      count: allContent.length,
      next: null, // Simplified for now
      previous: null,
      results: allContent
    };
  }

  async getCinemaItem(id: string): Promise<Cinema> {
    const response = await apiRequest(`/content/${id}`);
    const responseData = response.data || response;
    
    // Handle new response structure with nested content
    if (responseData.content) {
      return this.transformContentToCinema(responseData.content);
    } else {
      // Fallback for old response structure
      return this.transformContentToCinema(responseData);
    }
  }

  async getFeaturedCinema(): Promise<Cinema[]> {
    const response = await apiRequest('/content/featured');
    const pageData = response.data || response;
    const allItems = pageData.content || pageData.results || [];
    const cinemaItems = allItems.filter((item: BackendContent) => item.contentType === 'CINEMA');
    return cinemaItems.map((item: BackendContent) => this.transformContentToCinema(item));
  }

  async getTrendingCinema(): Promise<Cinema[]> {
    const response = await apiRequest('/content/trending');
    const pageData = response.data || response;
    const allItems = pageData.content || pageData.results || [];
    const cinemaItems = allItems.filter((item: BackendContent) => item.contentType === 'CINEMA');
    return cinemaItems.map((item: BackendContent) => this.transformContentToCinema(item));
  }

  async getMyCinema(filters: MediaFilter = {}): Promise<PaginatedResponse<Cinema>> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          params.append(key, value.toString());
        }
      }
    });

    const queryString = params.toString();
    return apiRequest(`/users/me/cinema${queryString ? `?${queryString}` : ''}`);
  }

  async getCinemaCategories(): Promise<{ value: string; label: string }[]> {
    return apiRequest('/content/cinema/categories');
  }

  async playCinema(id: string): Promise<{
    video_url: string;
    duration: number;
    duration_formatted: string;
    quality: string;
    subtitles: string[];
  }> {
    return apiRequest(`/content/cinema/${id}/play`);
  }

  async interactWithCinema(id: string, interactionData: {
    interaction_type: 'like' | 'bookmark' | 'watch' | 'rate';
    rating?: number;
    watch_progress?: number;
  }): Promise<{
    message: string;
    interaction?: MediaInteraction;
  }> {
    // Convert to the format expected by Spring Boot backend
    const requestData = {
      interactionType: interactionData.interaction_type,
      rating: interactionData.rating,
      watchProgress: interactionData.watch_progress,
    };
    return apiRequest(`/content/${id}/interact`, {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
  }

  async createCinema(formData: FormData): Promise<Cinema> {
    return apiRequest('/content/cinema', {
      method: 'POST',
      body: formData
    });
  }

  async updateCinema(id: string, data: FormData | Record<string, unknown>): Promise<Cinema> {
    // Handle both FormData and JSON updates
    if (data instanceof FormData) {
      return apiRequest(`/content/cinema/${id}`, {
        method: 'PATCH',
        body: data,
      });
    } else {
      // For JSON updates (when no files involved)
      return apiRequest(`/content/cinema/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    }
  }

  async deleteCinema(id: string): Promise<void> {
    await apiRequest(`/content/cinema/${id}`, {
      method: 'DELETE',
    });
  }

  // ===== CONTENT METHODS =====

  async getContent(filters: MediaFilter = {}): Promise<PaginatedResponse<Content>> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          // Map frontend parameters to backend parameters
          let backendKey = key;
          let backendValue = value.toString();
          
          if (key === 'page_size') {
            backendKey = 'size';
          } else if (key === 'page') {
            // Convert 1-based frontend pagination to 0-based backend pagination
            backendValue = Math.max(0, parseInt(value.toString()) - 1).toString();
          } else if (key === 'status' && value === 'published') {
            backendValue = 'PUBLISHED';
          }
          
          params.append(backendKey, backendValue);
        }
      }
    });

    const queryString = params.toString();
    const response = await apiRequest(`/content/contents${queryString ? `?${queryString}` : ''}`);
    
    // Transform the response to match frontend interface
    const pageData = response.data || response;
    return {
      count: pageData.totalElements || pageData.count || 0,
      next: pageData.hasNext ? 'next' : null,
      previous: pageData.hasPrevious ? 'previous' : null,
      results: (pageData.content || pageData.results || []).map((item: BackendContent) => this.transformBackendContent(item))
    };
  }

  async getContentItem(id: string): Promise<Content> {
    const response = await apiRequest(`/content/${id}`);
    const responseData = response.data || response;
    
    // Handle new response structure with interaction counts
    if (responseData.content) {
      const contentData = responseData.content;
      const transformedContent = this.transformBackendContent(contentData);
      
      // Add interaction counts to the content
      transformedContent.likeCount = responseData.likeCount || 0;
      transformedContent.like_count = responseData.likeCount || 0; // alias
      transformedContent.viewCount = responseData.viewCount || 0;
      transformedContent.view_count = responseData.viewCount || 0; // alias
      
      return transformedContent;
    } else {
      // Fallback for old response structure
      return this.transformBackendContent(responseData);
    }
  }

  async getFeaturedContent(): Promise<Content[]> {
    const response = await apiRequest('/content/featured');
    const pageData = response.data || response;
    const allItems = pageData.content || pageData.results || [];
    const contentItems = allItems.filter((item: BackendContent) => 
      ['CONTENT', 'PODCAST', 'ANIMATION', 'SNEAK_PEEK', 'LIVE_VIDEO'].includes(item.contentType)
    );
    return contentItems.map((item: BackendContent) => this.transformBackendContent(item));
  }

  async getTrendingContent(): Promise<Content[]> {
    const response = await apiRequest('/content/trending');
    const pageData = response.data || response;
    const allItems = pageData.content || pageData.results || [];
    const contentItems = allItems.filter((item: BackendContent) => 
      ['CONTENT', 'PODCAST', 'ANIMATION', 'SNEAK_PEEK', 'LIVE_VIDEO'].includes(item.contentType)
    );
    return contentItems.map((item: BackendContent) => this.transformBackendContent(item));
  }

  async getMyContent(filters: MediaFilter = {}): Promise<PaginatedResponse<Content>> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          params.append(key, value.toString());
        }
      }
    });

    const queryString = params.toString();
    return apiRequest(`/content/my_content/${queryString ? `?${queryString}` : ''}`);
  }

  async getContentCategories(): Promise<{ value: string; label: string }[]> {
    return apiRequest('/content/categories/');
  }

  async playContent(id: string): Promise<{
    video_url: string;
    duration: number;
    duration_formatted: string;
    quality: string;
    subtitles: string[];
    live_stream_url?: string;
  }> {
    return apiRequest(`/content/${id}/play_now/`);
  }

  async interactWithContent(id: string, interactionData: {
    interactionType: 'like' | 'bookmark' | 'watch' | 'rate';
    rating?: number;
    watchProgress?: number;
  }): Promise<{
    message: string;
    interaction?: MediaInteraction;
  }> {
    return apiRequest(`/content/${id}/interact`, {
      method: 'POST',
      body: JSON.stringify(interactionData),
    });
  }

  async getUserInteractions(id: string): Promise<{
    liked: boolean;
    bookmarked: boolean;
  }> {
    if (!id || id === 'undefined') {
      throw new Error('Invalid content ID provided');
    }
    
    const response = await apiRequest(`/content/${id}/interactions`, {
      method: 'GET',
    });
    return response.data;
  }

  async recordView(id: string): Promise<{
    viewId: string;
    message: string;
  }> {
    if (!id || id === 'undefined') {
      throw new Error('Invalid content ID provided for view recording');
    }
    
    const response = await apiRequest(`/content/${id}/views`, {
      method: 'POST',
    });
    return response.data;
  }

  async updateViewProgress(viewId: string, viewDuration: number, completionPercentage: number): Promise<void> {
    await apiRequest(`/content/views/${viewId}/progress`, {
      method: 'PUT',
      body: JSON.stringify({
        viewDuration,
        completionPercentage,
      }),
    });
  }

  async createContent(formData: FormData): Promise<Content> {
    return apiRequest('/content/', {
      method: 'POST',
      body: formData,
    });
  }

  async updateContent(id: string, data: FormData | Record<string, unknown>): Promise<Content> {
    // Handle both FormData and JSON updates
    if (data instanceof FormData) {
      return apiRequest(`/content/${id}/`, {
        method: 'PATCH',
        body: data,
      });
    } else {
      // For JSON updates (when no files involved)
      return apiRequest(`/content/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    }
  }

  async deleteContent(id: string): Promise<void> {
    await apiRequest(`/content/${id}/`, {
      method: 'DELETE',
    });
  }

  // ===== GENERAL MEDIA METHODS =====

  async getAllMedia(filters: MediaFilter = {}): Promise<{ cinema: PaginatedResponse<Cinema>; content: PaginatedResponse<Content> }> {
    // Get both cinema and content in parallel
    const [cinemaResponse, contentResponse] = await Promise.all([
      this.getCinema(filters),
      this.getContent(filters)
    ]);

    return {
      cinema: cinemaResponse,
      content: contentResponse
    };
  }

  async getHeroMedia(_type: 'cinema' | 'content' = 'cinema'): Promise<HeroMediaResponse> {
    // Get hero content from featured content
    const response = await apiRequest('/content/featured?page=0&size=1');
    const pageData = response.data || response;
    const featuredItems = pageData.content || pageData.results || [];
    
    if (featuredItems.length === 0) {
      throw new Error('No featured content available for hero section');
    }

    const heroItem = featuredItems[0];
    return {
      id: heroItem.id,
      title: heroItem.title,
      description: heroItem.description || '',
      short_description: heroItem.description || '',
      category: heroItem.category || 'General',
      thumbnail: heroItem.thumbnailUrl || '',
      banner: heroItem.thumbnailUrl || '',
      type: heroItem.contentType === 'CINEMA' ? 'cinema' : 'content',
      duration_formatted: this.formatDuration(heroItem.durationSeconds || 0),
      view_count: heroItem.viewCount || 0,
      like_count: heroItem.likeCount || 0,
      average_rating: 0, // Not available in backend yet
      is_trending: false, // Would need additional logic
      is_featured: heroItem.isFeatured || false
    };
  }

  async searchMedia(query: string, filters: {
    type?: 'cinema' | 'content' | 'all';
    category?: string;
    tags?: string;
    author?: string;
  } = {}): Promise<SearchResults> {
    const params = new URLSearchParams({ q: query });
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    return apiRequest(`/search/?${params.toString()}`);
  }

  async getMediaStats(): Promise<MediaStats> {
    return apiRequest('/stats/');
  }

  async getMediaLibrary(): Promise<MediaLibrary> {
    return apiRequest('/library/');
  }

  async trackMediaView(mediaType: 'cinema' | 'content', mediaId: string, viewData: {
    watch_duration?: number;
    completion_percentage?: number;
    quality_watched?: string;
    device_type?: 'desktop' | 'mobile' | 'tablet' | 'tv' | 'other';
  } = {}): Promise<{ message: string }> {
    return apiRequest(`/track-view/${mediaType}/${mediaId}/`, {
      method: 'POST',
      body: JSON.stringify(viewData),
    });
  }

  // ===== COLLECTIONS AND PLAYLISTS =====

  async getCollections(filters: MediaFilter = {}): Promise<PaginatedResponse<MediaCollection>> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          params.append(key, value.toString());
        }
      }
    });

    const queryString = params.toString();
    return apiRequest(`/collections/${queryString ? `?${queryString}` : ''}`);
  }

  async createCollection(data: {
    name: string;
    description: string;
    is_public: boolean;
  }): Promise<MediaCollection> {
    return apiRequest('/collections/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async addcinemaToCollection(collectionId: string, cinemaId: string): Promise<{ message: string }> {
    return apiRequest(`/collections/${collectionId}/add_cinema/`, {
      method: 'POST',
      body: JSON.stringify({ cinema_id: cinemaId }),
    });
  }

  async addContentToCollection(collectionId: string, contentId: string): Promise<{ message: string }> {
    return apiRequest(`/collections/${collectionId}/add_content/`, {
      method: 'POST',
      body: JSON.stringify({ content_id: contentId }),
    });
  }

  async getPlaylists(filters: MediaFilter = {}): Promise<PaginatedResponse<Playlist>> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          params.append(key, value.toString());
        }
      }
    });

    const queryString = params.toString();
    return apiRequest(`/playlists/${queryString ? `?${queryString}` : ''}`);
  }

  async createPlaylist(data: {
    name: string;
    description: string;
    is_public: boolean;
  }): Promise<Playlist> {
    return apiRequest('/playlists/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ===== DATA TRANSFORMATION METHODS =====
  
  private transformContentToCinema(backendContent: BackendContent): Cinema {
    return {
      id: backendContent.id,
      title: backendContent.title,
      slug: backendContent.slug,
      description: backendContent.description || '',
      short_description: backendContent.description || '',
      category: backendContent.category || 'General',
      contentType: 'CINEMA',
      thumbnailUrl: backendContent.thumbnailUrl || '',
      thumbnail: backendContent.thumbnailUrl || '',
      mediaUrl: backendContent.mediaUrl,
      previewUrl: backendContent.previewUrl,
      trailerUrl: backendContent.trailerUrl,
      author: backendContent.author || { id: '', username: 'unknown', first_name: 'Unknown', last_name: 'User', email: '', name: 'Unknown User' },
      status: backendContent.status || 'PUBLISHED',
      isFeatured: backendContent.isFeatured || false,
      is_featured: backendContent.isFeatured || false,
      isPremium: backendContent.isPremium || false,
      is_premium: backendContent.isPremium || false,
      viewCount: backendContent.viewCount || 0,
      view_count: backendContent.viewCount || 0,
      likeCount: backendContent.likeCount || 0,
      like_count: backendContent.likeCount || 0,
      durationSeconds: backendContent.durationSeconds || 0,
      duration: backendContent.durationSeconds || 0,
      duration_formatted: this.formatDuration(backendContent.durationSeconds || 0),
      fileSizeMb: backendContent.fileSizeMb || 0,
      file_size: backendContent.fileSizeMb || 0,
      file_size_formatted: this.formatFileSize(backendContent.fileSizeMb || 0),
      creatorName: backendContent.creatorName,
      channelName: backendContent.channelName,
      externalId: backendContent.externalId,
      imdbId: backendContent.imdbId,
      createdAt: backendContent.createdAt || new Date().toISOString(),
      created_at: backendContent.createdAt || new Date().toISOString(),
      updatedAt: backendContent.updatedAt || new Date().toISOString(),
      updated_at: backendContent.updatedAt || new Date().toISOString(),
      publishedAt: backendContent.publishedAt,
      published_at: backendContent.publishedAt,
    };
  }

  private transformBackendContent(backendContent: BackendContent): Content {
    // Extract series information from episode.series nested structure
    const episode = backendContent.episode;
    const series = episode?.series;
    const isPartOfSeries = backendContent.partOfSeries || (episode && series) || false;
    
    const seriesInfo = isPartOfSeries && series ? {
      isPartOfSeries: true,
      seriesId: series.id,
      seriesTitle: series.title,
      seasonNumber: backendContent.seasonNumber || episode?.season?.seasonNumber,
      episodeNumber: backendContent.episodeNumber || episode?.episodeNumber,
      totalSeasons: series.totalSeasons || (series.seasons ? series.seasons.length : 0),
      totalEpisodes: series.totalEpisodes,
      seriesType: series.seriesType
    } : {
      isPartOfSeries: false
    };

    return {
      id: backendContent.id,
      title: backendContent.title,
      slug: backendContent.slug,
      description: backendContent.description || '',
      short_description: backendContent.description || '',
      category: backendContent.category || 'General',
      contentType: backendContent.contentType || 'CONTENT',
      content_type: backendContent.contentType || 'CONTENT',
      tags: backendContent.tags || [],
      thumbnailUrl: backendContent.thumbnailUrl || '',
      thumbnail: backendContent.thumbnailUrl || '',
      mediaUrl: backendContent.mediaUrl,
      previewUrl: backendContent.previewUrl,
      trailerUrl: backendContent.trailerUrl,
      contentBody: backendContent.contentBody,
      author: backendContent.author || { id: '', username: 'unknown', first_name: 'Unknown', last_name: 'User', email: '', name: 'Unknown User' },
      status: backendContent.status || 'PUBLISHED',
      isFeatured: backendContent.isFeatured || false,
      is_featured: backendContent.isFeatured || false,
      isPremium: backendContent.isPremium || false,
      is_premium: backendContent.isPremium || false,
      isLive: backendContent.isLive || false,
      is_live: backendContent.isLive || false,
      viewCount: backendContent.viewCount || 0,
      view_count: backendContent.viewCount || 0,
      likeCount: backendContent.likeCount || 0,
      like_count: backendContent.likeCount || 0,
      durationSeconds: backendContent.durationSeconds || 0,
      duration: backendContent.durationSeconds || 0,
      duration_formatted: this.formatDuration(backendContent.durationSeconds || 0),
      fileSizeMb: backendContent.fileSizeMb || 0,
      file_size: backendContent.fileSizeMb || 0,
      file_size_formatted: this.formatFileSize(backendContent.fileSizeMb || 0),
      subtitles: backendContent.subtitles || [],
      creatorName: backendContent.creatorName,
      creator: backendContent.creatorName,
      channelName: backendContent.channelName,
      externalId: backendContent.externalId,
      imdbId: backendContent.imdbId,
      liveStatus: backendContent.liveStatus,
      scheduledStartTime: backendContent.scheduledStartTime,
      actualStartTime: backendContent.actualStartTime,
      currentViewers: backendContent.currentViewers,
      maxViewers: backendContent.maxViewers,
      is_liked: backendContent.is_liked,
      is_bookmarked: backendContent.is_bookmarked,
      user_rating: backendContent.user_rating,
      watch_progress: backendContent.watch_progress,
      createdAt: backendContent.createdAt || new Date().toISOString(),
      created_at: backendContent.createdAt || new Date().toISOString(),
      updatedAt: backendContent.updatedAt || new Date().toISOString(),
      updated_at: backendContent.updatedAt || new Date().toISOString(),
      publishedAt: backendContent.publishedAt,
      published_at: backendContent.publishedAt,
      ...seriesInfo
    };
  }

  // Transform series to Cinema format for unified display
  // This method creates a series "container" item that represents the series as a whole
  private transformSeriesToCinema(series: any): Cinema { // eslint-disable-line @typescript-eslint/no-explicit-any
    return {
      id: `series-${series.id}`, // Prefix with 'series-' to distinguish from regular content
      title: series.title,
      slug: series.slug,
      description: series.description || '',
      short_description: series.description || '',
      category: series.category || 'General',
      contentType: 'CINEMA',
      thumbnailUrl: series.thumbnailUrl || series.bannerUrl || '',
      thumbnail: series.thumbnailUrl || series.bannerUrl || '',
      mediaUrl: '', // Series don't have direct media URLs - will route to first episode
      previewUrl: series.bannerUrl,
      trailerUrl: '',
      author: series.author || { id: '', username: 'unknown', first_name: 'Unknown', last_name: 'User', email: '', name: 'Unknown User' },
      status: series.status || 'PUBLISHED',
      isFeatured: series.isFeatured || false,
      is_featured: series.isFeatured || false,
      isPremium: series.isPremium || false,
      is_premium: series.isPremium || false,
      viewCount: 0, // Series don't have direct view counts
      view_count: 0,
      likeCount: 0, // Series don't have direct like counts
      like_count: 0,
      durationSeconds: 0, // Series don't have direct duration
      duration: 0,
      duration_formatted: `${series.totalEpisodes} episodes`,
      fileSizeMb: 0,
      file_size: 0,
      file_size_formatted: '',
      creatorName: series.author?.name || series.author?.username || '',
      channelName: '',
      externalId: '',
      imdbId: '',
      createdAt: series.createdAt || new Date().toISOString(),
      created_at: series.createdAt || new Date().toISOString(),
      updatedAt: series.updatedAt || new Date().toISOString(),
      updated_at: series.updatedAt || new Date().toISOString(),
      publishedAt: series.publishedAt,
      published_at: series.publishedAt,
      // Series-specific properties
      isPartOfSeries: true,
      seriesId: series.id,
      seriesTitle: series.title,
      totalSeasons: series.totalSeasons,
      totalEpisodes: series.totalEpisodes,
      seriesType: series.seriesType,
    };
  }

  private formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '0m';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds > 0 ? remainingSeconds + 's' : ''}`;
    } else {
      return `${remainingSeconds}s`;
    }
  }

  private formatFileSize(sizeMb: number): string {
    if (!sizeMb || sizeMb <= 0) return '0 MB';
    
    if (sizeMb >= 1024) {
      return `${(sizeMb / 1024).toFixed(1)} GB`;
    } else {
      return `${sizeMb.toFixed(1)} MB`;
    }
  }

  // ===== COMBINED CONTENT + SERIES METHODS =====
  
  async getCinemaCombined(filters: MediaFilter = {}): Promise<UnifiedContentItem[]> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          let backendKey = key;
          let backendValue = value.toString();
          
          if (key === 'page_size') {
            backendKey = 'size';
          } else if (key === 'page') {
            backendValue = Math.max(0, parseInt(value.toString()) - 1).toString();
          }
          
          params.append(backendKey, backendValue);
        }
      }
    });

    const queryString = params.toString();
    const response = await apiRequest(`/content/cinema-combined${queryString ? `?${queryString}` : ''}`);
    return response.data || [];
  }

  async getContentsCombined(filters: MediaFilter = {}): Promise<UnifiedContentItem[]> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          let backendKey = key;
          let backendValue = value.toString();
          
          if (key === 'page_size') {
            backendKey = 'size';
          } else if (key === 'page') {
            backendValue = Math.max(0, parseInt(value.toString()) - 1).toString();
          }
          
          params.append(backendKey, backendValue);
        }
      }
    });

    const queryString = params.toString();
    const response = await apiRequest(`/content/contents-combined${queryString ? `?${queryString}` : ''}`);
    return response.data || [];
  }

  async getPodcastsCombined(filters: MediaFilter = {}): Promise<UnifiedContentItem[]> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          let backendKey = key;
          let backendValue = value.toString();
          
          if (key === 'page_size') {
            backendKey = 'size';
          } else if (key === 'page') {
            backendValue = Math.max(0, parseInt(value.toString()) - 1).toString();
          }
          
          params.append(backendKey, backendValue);
        }
      }
    });

    const queryString = params.toString();
    const response = await apiRequest(`/content/podcasts-combined${queryString ? `?${queryString}` : ''}`);
    return response.data || [];
  }

  async getAnimationsCombined(filters: MediaFilter = {}): Promise<UnifiedContentItem[]> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          let backendKey = key;
          let backendValue = value.toString();
          
          if (key === 'page_size') {
            backendKey = 'size';
          } else if (key === 'page') {
            backendValue = Math.max(0, parseInt(value.toString()) - 1).toString();
          }
          
          params.append(backendKey, backendValue);
        }
      }
    });

    const queryString = params.toString();
    const response = await apiRequest(`/content/animations-combined${queryString ? `?${queryString}` : ''}`);
    return response.data || [];
  }

  async getMediaCombined(filters: MediaFilter = {}): Promise<UnifiedContentItem[]> {
    try {
      // Fetch both cinema and content combined data
      const [cinemaData, contentData] = await Promise.all([
        this.getCinemaCombined(filters),
        this.getContentsCombined(filters)
      ]);

      // Combine and sort by creation date (newest first)
      const combined = [...cinemaData, ...contentData];
      return combined.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error) {
      console.error('Error fetching combined media:', error);
      throw error;
    }
  }
}

// ===== EXPORT =====
const mediaService = new MediaService();
export default mediaService;
